import { registerLocaleData } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Localization, LOCALIZATIONS } from '@cue/translate';
import { MessageService } from '@progress/kendo-angular-l10n';
import { RtlProvider } from '@cue/utilities';

export type FirstDayOfWeek = 'monday' | 'sunday';

type LocaleCode = keyof typeof AVAILABLE_LOCALE_IDS;
type LocaleModule = { default: any };
type LocaleLoader = () => [Promise<LocaleModule>, Promise<unknown>];

const AVAILABLE_LOCALE_IDS = {
  fr: 'fr',
  no: 'nb',
  nb: 'nb',
  en: 'en',
  de: 'de',
  cs: 'cs',
  ca: 'ca',
  ua: 'ua',
  es: 'es',
  ar: 'ar',
  pl: 'pl',
} as const;

const AVAILABLE_LOCALE_LOADERS: Record<LocaleCode, LocaleLoader> = {
  ca: () => [import('@angular/common/locales/ca'), import('@progress/kendo-angular-intl/locales/ca/all')],
  ua: () => [import('@angular/common/locales/ru-UA'), import('@progress/kendo-angular-intl/locales/ru-UA/all')],
  fr: () => [import('@angular/common/locales/fr'), import('@progress/kendo-angular-intl/locales/fr/all')],
  no: () => [import('@angular/common/locales/nb'), import('@progress/kendo-angular-intl/locales/nb/all')],
  nb: () => [import('@angular/common/locales/nb'), import('@progress/kendo-angular-intl/locales/nb/all')],
  en: () => [import('@angular/common/locales/en'), import('@progress/kendo-angular-intl/locales/en/all')],
  de: () => [import('@angular/common/locales/de'), import('@progress/kendo-angular-intl/locales/de/all')],
  cs: () => [import('@angular/common/locales/cs'), import('@progress/kendo-angular-intl/locales/cs/all')],
  es: () => [import('@angular/common/locales/es'), import('@progress/kendo-angular-intl/locales/es/all')],
  ar: () => [import('@angular/common/locales/ar'), import('@progress/kendo-angular-intl/locales/ar/all')],
  pl: () => [import('@angular/common/locales/pl'), import('@progress/kendo-angular-intl/locales/pl/all')],
};

const loadedLocaleCodes = new Set<string>();

function normalizeCode(language: string) {
  return language.toLowerCase().split('-')[0];
}

function getLocaleCode(language: string | null | undefined): LocaleCode {
  if (!language) {
    return 'en';
  }

  const normalizedCode = normalizeCode(language);
  if (normalizedCode in AVAILABLE_LOCALE_IDS) {
    return normalizedCode as LocaleCode;
  }

  return 'en';
}

export function getPreferredLanguageCode() {
  const langFromLocalStorage = localStorage.getItem('assist-lang');
  if (langFromLocalStorage) {
    return getLocaleCode(langFromLocalStorage);
  }

  const userLang = navigator.language || (navigator as any).userLanguage;
  return getLocaleCode(userLang);
}

export function resolveLocaleId(language: string | null | undefined) {
  return AVAILABLE_LOCALE_IDS[getLocaleCode(language)] ?? 'en';
}

export async function ensureLocaleDataLoaded(language: string | null | undefined) {
  const localeCode = getLocaleCode(language);

  if (loadedLocaleCodes.has(localeCode)) {
    return AVAILABLE_LOCALE_IDS[localeCode];
  }

  const [angularLocaleData] = await Promise.all(AVAILABLE_LOCALE_LOADERS[localeCode]());
  registerLocaleData(angularLocaleData.default, AVAILABLE_LOCALE_IDS[localeCode]);
  loadedLocaleCodes.add(localeCode);

  return AVAILABLE_LOCALE_IDS[localeCode];
}

@Injectable({
  providedIn: 'root',
})
export class LocaleService extends RtlProvider {
  #translocoService = inject(TranslocoService);
  #messageService = inject(MessageService);
  #localizations = inject(LOCALIZATIONS, { optional: true }) as unknown as Localization[][];

  #rtl = signal(false);
  override rtl = this.#rtl.asReadonly();

  public rtlLanguages: string[] = ['ar', 'he', 'fa', 'ur'];
  public languageItems: Array<{ text: string; flag: string }> = [
    { text: 'CS', flag: 'cs.svg' },
    { text: 'EN', flag: 'en.svg' },
    { text: 'ES', flag: 'es.svg' },
    { text: 'DE', flag: 'ge.svg' },
    { text: 'FR', flag: 'fr.svg' },
    { text: 'CA', flag: 'ca.svg' },
    { text: 'UA', flag: 'ua.svg' },
    { text: 'AR', flag: 'ar.svg' },
    { text: 'PL', flag: 'pl.svg' },
  ].filter(
    (lang) => this.#translocoService.getAvailableLangs().find((l) => l === lang.text.toLowerCase()) !== undefined
  );

  getLangFromCode(code: string) {
    const availableLangs: any[] = this.#translocoService.getAvailableLangs();
    const found = availableLangs.find((x) => x == code);
    return (
      this.languageItems.find((x) => x.text == (found ?? this.#translocoService.getDefaultLang()).toUpperCase())! ??
      this.currentLanguage
    );
  }

  get currentLanguage(): { text: string; flag: string } {
    const availableLangs: any[] = this.#translocoService.getAvailableLangs();
    const langFromLocalStorage = localStorage.getItem('assist-lang');
    if (langFromLocalStorage != undefined) {
      const found = availableLangs.find((x) => x == langFromLocalStorage);
      const selectedLanguage = found ?? this.#translocoService.getDefaultLang();
      void ensureLocaleDataLoaded(selectedLanguage);
      if (found) {
        const isRtl = this.rtlLanguages.includes(found);
        this.#rtl.set(isRtl);
        this.#messageService.notify(isRtl);
      }
      return this.languageItems.find((x) => x.text == selectedLanguage.toUpperCase())!;
    } else {
      const found = availableLangs.find((x) => x == navigator.language.slice(0, 2));
      const selectedLanguage = found ?? this.#translocoService.getDefaultLang();
      void ensureLocaleDataLoaded(selectedLanguage);
      if (found) {
        const isRtl = this.rtlLanguages.includes(found);
        this.#rtl.set(isRtl);
        this.#messageService.notify(isRtl);
      }
      return this.languageItems.find((x) => x.text == selectedLanguage.toUpperCase())!;
    }
  }

  set currentLanguage(value: { text: string; flag: string }) {
    const availableLangs: any[] = this.#translocoService.getAvailableLangs();
    const found = availableLangs.find((x) => x == value.text.toLowerCase());

    if (found) {
      const translation = this.#localizations[0].find((x) => x.language == found)!;
      Promise.all([translation.valueGetter(), ensureLocaleDataLoaded(found)]).then(([x]) => {
        localStorage.setItem('assist-lang', found);
        this.#translocoService.setTranslation(x, translation.language, {
          merge: false,
        });
        this.#translocoService.setActiveLang(found);

        const isRtl = this.rtlLanguages.includes(found);
        this.#rtl.set(isRtl);
        this.#messageService.notify(isRtl);
      });
    }
  }

  static availableCodes = {
    fr: () => AVAILABLE_LOCALE_IDS.fr,
    no: () => AVAILABLE_LOCALE_IDS.no,
    nb: () => AVAILABLE_LOCALE_IDS.nb,
    en: () => AVAILABLE_LOCALE_IDS.en,
    de: () => AVAILABLE_LOCALE_IDS.de,
    cs: () => AVAILABLE_LOCALE_IDS.cs,
    ca: () => AVAILABLE_LOCALE_IDS.ca,
    ua: () => AVAILABLE_LOCALE_IDS.ua,
    es: () => AVAILABLE_LOCALE_IDS.es,
    ar: () => AVAILABLE_LOCALE_IDS.ar,
    pl: () => AVAILABLE_LOCALE_IDS.pl,
  };

  #firstDayOfWeek: FirstDayOfWeek = 'monday';
  getFirstDayOfWeek(): FirstDayOfWeek {
    return this.#firstDayOfWeek;
  }

  setFirstDayOfWeek(day: FirstDayOfWeek) {
    this.#firstDayOfWeek = day;
  }

  getLocaleId() {
    return resolveLocaleId(getPreferredLanguageCode());
  }
}
